import React from 'react';
import { BookOpen, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const GasolineWorkbookSeries = () => {
  const workbooks = [
    {
      id: 1,
      number: 'I',
      title: 'Smoldering Coals of Past Experiences',
      subtitle: 'GASOLINE PART ONE',
      tagline: 'Healing Through Poetry & Breaking the Cycle of Violence',
      amazonLink: 'https://a.co/d/0fm0QG50',
      images: [
        'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/7yvdds6x_Gasoline%201%20-%20Workbook.jpg',
        'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/3x0rn3w8_Gasoline%201%20Workbook.jpg'
      ],
      description: [
        'The first installment of Gasoline serves as an entry point into deep self-exploration, guiding participants to uncover the unconscious decisions they made in response to abuse, neglect, and early life trauma. These decisions, often formed in moments of fear, pain, or survival, can sound like: "this will never happen to me again," "I have to stay guarded," or "I need to become stronger so no one can hurt me." While these beliefs were once protective, they often evolve into automatic patterns that shape behavior, relationships, and identity without conscious awareness.',
        'Through the use of hip-hop and reflective writing, participants are given a safe and structured space to examine these internal commitments. Poetry becomes the tool that allows buried thoughts, emotions, and memories to surface in an authentic and unfiltered way. As individuals begin to put language to their experiences, they gain clarity on how their past has influenced their present choices.',
        'This process is not about judgment. It is about awareness. Participants are encouraged to trace their behaviors back to their origins, helping them understand not just what they do, but why they do it. By bringing these unconscious survival strategies into the light, Gasoline I creates an opportunity to question whether they are still serving a purpose or if they have become barriers to growth.',
        'The ultimate goal of this first book is to lay a strong foundation for healing. By increasing self-awareness and emotional honesty, participants begin the work of releasing outdated survival patterns and opening themselves up to healthier ways of thinking, responding, and living. Through poetry, reflection, and guided exploration, healing begins with truth and the courage to face it.'
      ],
      purpose: 'Uncovering unconscious survival decisions formed in trauma',
      color: 'from-[#3B82F6] to-[#60A5FA]'
    },
    {
      id: 2,
      number: 'II',
      title: 'Roots of Selfishness',
      subtitle: 'GASOLINE TWO',
      tagline: 'Every crime has roots. What is not healed is repeated.',
      amazonLink: 'https://a.co/d/06180gC3',
      images: [
        'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/4r3jueoo_Gasoline%202%20-%20Workbook.jpg',
        'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/ty2x734u_Gasoline%202%20Workbook.jpg'
      ],
      description: [
        'The second installment of Gasoline moves participants beyond awareness and into deeper accountability by examining the cycle of victimization. Building on the foundation established in Gasoline I, this phase challenges individuals to confront a difficult but necessary truth: those who have been hurt can, without healing, begin to hurt others, often in ways that mirror their own experiences.',
        'At the core of this work is the understanding that "what is not healed is repeated." Survival-based thinking, developed in response to trauma, can evolve into patterns of behavior that prioritize self-protection at the expense of others. These patterns may show up as manipulation, control, emotional detachment, aggression, or a lack of empathy. The curriculum identifies these as rooted forms of selfishness. Not selfishness in a shallow sense, but as a learned response to pain, fear, and unmet needs.',
        'Through guided reflection, writing exercises, and hip-hop based expression, participants are encouraged to take an honest look at how their actions have impacted others. This process is not about shame. It is about ownership. By tracing harmful behaviors back to their origin, individuals begin to see the connection between their past victimization and their present choices.',
        'Gasoline II creates space for participants to wrestle with accountability in a way that is both challenging and transformative. It pushes them to move past justification and into responsibility, helping them recognize where they have repeated cycles they once resented. At the same time, it reinforces that acknowledgment is a powerful step toward change.',
        'The goal of this phase is to interrupt the cycle. By increasing awareness of these patterns and taking responsibility for their impact, participants are empowered to choose differently. They begin to replace reactive, survival-driven behaviors with intentional actions rooted in growth, empathy, and self-control. In Gasoline II, healing deepens by confronting the truth: breaking the cycle starts with recognizing your role in it and choosing to change it.'
      ],
      purpose: 'Taking accountability for how unhealed trauma perpetuates harm',
      color: 'from-[#A855F7] to-[#C084FC]'
    },
    {
      id: 3,
      number: 'III',
      title: 'Broken Lenses',
      subtitle: 'GASOLINE THREE',
      tagline: 'Healing is not erasing the cracks. It is learning to see through them differently.',
      amazonLink: 'https://a.co/d/0acQ08QF',
      images: [
        'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/xi9xu3hr_Gasoline%203%20-%20Workbook.jpg',
        'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/y9vcvl79_Gasoline%203%20Workbook.jpg'
      ],
      description: [
        'The final installment of Gasoline focuses on transformation through the renewal of the mind. After uncovering unconscious beliefs and taking accountability for harmful patterns, this phase challenges participants to examine the way trauma has shaped how they see themselves, others, and the world around them. These "broken lenses" are distorted thinking patterns formed through pain, fear, and survival. These filters influence perception, reactions, and decision-making.',
        'Participants explore how these distorted lenses can lead to misinterpretation in everyday situations. They may see threat where there may be none, respond with defensiveness instead of understanding, or react from past wounds rather than present reality. These patterns often operate automatically, especially in moments of stress, conflict, or emotional pressure.',
        'Gasoline III introduces biblical principles as tools for reframing and renewing these thought processes. Through guided reflection, writing, and application, participants are encouraged to challenge the beliefs that were formed in trauma and replace them with truth-based perspectives that promote clarity, self-control, and peace. This is not about religion as routine, but about transformation through truth. It is about learning how to align thoughts with principles that support healing and growth.',
        'This phase emphasizes intentional response over automatic reaction. Participants learn how to pause, reflect, and choose a different way of thinking and responding, even in situations that once triggered survival-based behaviors. It is about building new mental pathways, ones that are no longer rooted in fear, but in understanding and purpose.',
        'The goal of Gasoline III is not to erase the past, but to change how it is seen and understood. Healing comes from learning to view life through a renewed lens, one that allows individuals to respond to the present without being controlled by the past. Through this process, participants begin to experience a deeper level of freedom, where transformation is not just something they understand, but something they live.'
      ],
      purpose: 'Renewing the mind through truth-based perspectives and intentional response',
      color: 'from-[#D4AF37] to-[#FFD700]'
    }
  ];

  return (
    <section id="workbooks" className="relative py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-6 py-2 mb-6">
            <BookOpen className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-bold text-sm">CURRICULUM</span>
          </div>
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            THE GASOLINE
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700]">
              WORKBOOK SERIES
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A three-part journey from awareness to transformation through trauma-informed writing, reflection, and self-exploration
          </p>
        </div>

        {/* Workbooks */}
        <div className="space-y-16">
          {workbooks.map((workbook, index) => (
            <div
              key={workbook.id}
              className={`bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-[#D4AF37]/20 rounded-3xl overflow-hidden hover:border-[#D4AF37] transition-all duration-300`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Images Side */}
                <div className={`p-8 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="grid grid-cols-2 gap-4">
                    {workbook.images.map((img, idx) => (
                      <img
                        key={img}
                        src={img}
                        alt={`${workbook.title} - ${idx + 1}`}
                        className="w-full rounded-xl shadow-2xl transform hover:scale-105 transition-transform duration-300"
                      />
                    ))}
                  </div>
                </div>

                {/* Content Side */}
                <div className={`p-8 md:p-12 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  {/* Number Badge */}
                  <div className={`inline-block bg-gradient-to-r ${workbook.color} text-[#0a0a0a] font-bold px-6 py-2 rounded-full text-sm mb-4`}>
                    GASOLINE {workbook.number}
                  </div>

                  <h3 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
                    {workbook.title}
                  </h3>
                  
                  <p className="text-xl text-[#D4AF37] mb-6 italic">
                    "{workbook.tagline}"
                  </p>

                  {/* Purpose */}
                  <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 mb-6">
                    <p className="text-sm font-bold text-[#D4AF37] mb-1">PURPOSE:</p>
                    <p className="text-gray-300 text-sm">{workbook.purpose}</p>
                  </div>

                  {/* Description Preview */}
                  <div className="space-y-4 mb-6">
                    <p className="text-gray-300 leading-relaxed">
                      {workbook.description[0]}
                    </p>
                    {workbook.description.length > 1 && (
                      <p className="text-gray-400 text-sm leading-relaxed">
                        {workbook.description[1].substring(0, 200)}...
                      </p>
                    )}
                  </div>

                  {/* Key Features */}
                  <div className="bg-[#0a0a0a]/50 border border-[#D4AF37]/20 rounded-lg p-4 mb-6">
                    <p className="text-sm font-bold text-white mb-3">INCLUDES:</p>
                    <ul className="space-y-2 text-sm text-gray-400">
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Trauma-informed writing exercises</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Hip-hop based reflection prompts</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Guided self-exploration activities</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mr-2 mt-0.5 flex-shrink-0" />
                        <span>Biblical principles for transformation</span>
                      </li>
                    </ul>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    {workbook.amazonLink && (
                      <Button
                        onClick={() => window.open(workbook.amazonLink, '_blank')}
                        data-testid={`order-workbook-amazon-${workbook.id}`}
                        className={`bg-gradient-to-r ${workbook.color} text-[#0a0a0a] font-bold px-6 py-6 text-base sm:text-lg transition-all duration-300 transform hover:scale-105`}
                      >
                        BUY ON AMAZON
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#1a1a1a] via-[#D4AF37]/10 to-[#1a1a1a] border border-[#D4AF37]/30 rounded-2xl p-8">
          <p className="text-2xl text-white font-semibold mb-4">
            Complete Gasoline Series Included in $60 Sponsorship
          </p>
          <p className="text-gray-400 mb-6">
            All three workbooks + Urbanation album delivered to the recipient of your choice
          </p>
          <Button
            onClick={() => window.open('https://thegardensofhope.org/donate-to-the-gardens-of-hope-donorbox/', '_blank')}
            data-testid="workbook-sponsor-cta"
            className="w-full sm:w-auto max-w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold px-6 sm:px-12 py-6 sm:py-7 text-base sm:text-xl whitespace-normal leading-tight break-words transition-all duration-300"
          >
            SPONSOR A TRANSFORMATION - $60
          </Button>
          <p className="text-gray-400 text-sm mt-4 max-w-2xl mx-auto">
            Donations processed through Gardens of Hope (501c)(3). Select "Sponsor a Transformation" and add recipient details in comments.
          </p>
        </div>
      </div>
    </section>
  );
};

export default GasolineWorkbookSeries;
